import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { User, Class } from '../types/alarm';
import { getOrderedMemberGroups } from '../utils/memberGroupOrder';
import {
  getAvatarInitialsFromEmail,
  getUiAvatarPlaceholderUrl,
  isUiAvatarsPlaceholderUrl
} from '../utils/uiAvatarPlaceholder';
import {
  formatPageZoomPercent,
  PAGE_ZOOM_CHANGED_EVENT,
  PAGE_ZOOM_MAX,
  PAGE_ZOOM_MIN,
  readPageZoom,
  stepPageZoom,
} from '../utils/pageZoom';
import { userStorageQuotaService } from '../services/userStorageQuotaService';
import { isStandardPlanOrAbove } from '../utils/licenseLimits';

interface HeaderProps {
  user: User;
  onLogout: () => void;
  onNavigate: (page: 'home' | 'alarm' | 'admin' | 'assignments' | 'points-table' | 'chat' | 'board' | 'license' | 'terms-of-service' | 'privacy-policy') => void;
  currentPage: 'home' | 'alarm' | 'admin' | 'assignments' | 'points-table' | 'chat' | 'board' | 'license' | 'terms-of-service' | 'privacy-policy';
  onUpdateUser: (updatedUser: User) => void;
  onProfileModalOpen: () => void;
  onClassManageModalOpen: () => void;
  classes: Class[];
  currentClass: Class | null;
  // 멤버 그룹 선택 (선택사항)
  memberGroups?: string[];
  currentMemberGroup?: string;
  onMemberGroupChange?: (memberGroup: string) => void;
  themeMode: 'dark' | 'light';
  onToggleTheme: () => void;
  // 포인트 페이지 메뉴 클릭 콜백들 (선택사항)
  onSeatAssignmentClick?: () => void;
  onLevelSettingsClick?: () => void;
  onPointManagementClick?: () => void;
  onItemCardSettingsClick?: () => void;
  onAssignmentSettingsClick?: () => void;
  onMemberProfileSettingsClick?: () => void;
  onDiceModalOpen?: () => void;
  // 알람 페이지 이동 함수 (선택사항)
  onAlarmNavigate?: () => void;
  // 플랜 업그레이드 모달 (선택사항)
  onUpgradeModalOpen?: () => void;
}

const Header: React.FC<HeaderProps> = ({ user, onLogout, onNavigate, currentPage, onUpdateUser, onProfileModalOpen, onClassManageModalOpen, classes, currentClass, memberGroups, currentMemberGroup, onMemberGroupChange, themeMode, onToggleTheme, onSeatAssignmentClick, onLevelSettingsClick, onPointManagementClick, onItemCardSettingsClick, onAssignmentSettingsClick, onMemberProfileSettingsClick, onDiceModalOpen, onAlarmNavigate, onUpgradeModalOpen }) => {
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [avatarBroken, setAvatarBroken] = useState(false);
  const [storageUsedBytes, setStorageUsedBytes] = useState<number | null>(null);
  const [storageUsageLoading, setStorageUsageLoading] = useState(false);
  const [pageZoom, setPageZoom] = useState(() => readPageZoom());

  const profileImageSrc =
    !avatarBroken && user.picture?.trim() && !isUiAvatarsPlaceholderUrl(user.picture.trim())
      ? user.picture.trim()
      : getUiAvatarPlaceholderUrl(getAvatarInitialsFromEmail(user.email), { size: 128 });

  useEffect(() => {
    setAvatarBroken(false);
  }, [user.picture, user.id]);

  const normalizedMemberGroups = getOrderedMemberGroups(
    memberGroups && memberGroups.length > 0 ? memberGroups : currentClass?.memberGroups,
    currentClass?.memberGroupOrder
  );
  // 그룹 선택기 표시: 그룹이 2개 이상인 경우 항상 표시 (프로 플랜이 아니어도 서버에 이미 있는 그룹은 표시)
  const canSelectAllMemberGroups = isStandardPlanOrAbove(user.userType);
  const showStorageUsage = isStandardPlanOrAbove(user.userType);
  const showMemberGroupSelector = normalizedMemberGroups.length > 1 && typeof onMemberGroupChange === 'function';

  // 그룹 선택 핸들러 (플랜 제한 적용: 스탠다드 이상만 다중 그룹 전환)
  const handleMemberGroupChange = (selectedGroup: string) => {
    if (!canSelectAllMemberGroups) {
      const firstGroup = normalizedMemberGroups[0];
      if (selectedGroup !== firstGroup) {
        // 다른 그룹 선택 시 플랜 업그레이드 모달 표시
        if (onUpgradeModalOpen) {
          onUpgradeModalOpen();
        }
        return;
      }
    }

    // 선택 가능한 그룹인 경우 변경
    onMemberGroupChange?.(selectedGroup);
  };
  const currentGroupValue = (currentMemberGroup || normalizedMemberGroups[0] || 'A').toString().trim().toUpperCase();
  
  // 그룹 이름 표시 헬퍼 함수
  const getGroupDisplayName = (groupKey: string): string => {
    return currentClass?.memberGroupNames?.[groupKey] || `${groupKey}그룹`;
  };

  // 클래스 데이터 로드 제거 - App.tsx에서 props로 전달받음

  // 화면 크기 변경 감지
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleMemberPhotoProfileSettingsOpen = useCallback(() => {
    if (onMemberProfileSettingsClick) {
      onMemberProfileSettingsClick();
      return;
    }
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('openMemberPhotoProfileSettings'));
    }
  }, [onMemberProfileSettingsClick]);

  useEffect(() => {
    if (!isMenuOpen || !user.id || !showStorageUsage) return;

    const cached = userStorageQuotaService.getCachedUsageBytes(user.id);
    if (cached != null) {
      setStorageUsedBytes(cached);
      setStorageUsageLoading(false);
      return;
    }

    let cancelled = false;
    setStorageUsageLoading(true);
    userStorageQuotaService
      .fetchUsageBytes(user.id)
      .then((bytes) => {
        if (!cancelled) setStorageUsedBytes(bytes);
      })
      .catch((error) => {
        console.error('스토리지 사용량 조회 실패:', error);
        if (!cancelled) setStorageUsedBytes(null);
      })
      .finally(() => {
        if (!cancelled) setStorageUsageLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isMenuOpen, user.id, showStorageUsage]);

  // 드롭다운 외부 클릭 시 닫기
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.hamburger-menu') && !target.closest('.menu-dropdown')) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      // mousedown 대신 click 이벤트 사용하여 onClick과 충돌 방지
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isMenuOpen]);

  useEffect(() => {
    const handlePageZoomChanged = (event: Event) => {
      const detail = (event as CustomEvent<{ zoom?: number }>).detail;
      if (typeof detail?.zoom === 'number') {
        setPageZoom(detail.zoom);
        return;
      }
      setPageZoom(readPageZoom());
    };

    window.addEventListener(PAGE_ZOOM_CHANGED_EVENT, handlePageZoomChanged as EventListener);
    return () => {
      window.removeEventListener(PAGE_ZOOM_CHANGED_EVENT, handlePageZoomChanged as EventListener);
    };
  }, []);

  const handlePageZoomOut = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setPageZoom(stepPageZoom(-1));
  };

  const handlePageZoomIn = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setPageZoom(stepPageZoom(1));
  };

  const handleHamburgerClick = () => {
    console.log('🔍 Header: 햄버거 버튼 클릭 - 현재 상태:', isMenuOpen);
    setIsMenuOpen(!isMenuOpen);
    console.log('🔍 Header: 햄버거 버튼 클릭 완료 - 새 상태:', !isMenuOpen);
  };

  // 클래스 관리 관련 함수들
  const handleClassManageClick = () => {
    onClassManageModalOpen();
  };

  // 화면 크기에 따른 표시 단계 결정
  const getDisplayStage = () => {
    if (currentPage === 'home') {
      // 홈 페이지에서는 3단계(사라지기) 제거
      if (windowWidth > 850) return 'name';      // 1단계: 클래스명
      return 'icon';                             // 2단계: 클래스 아이콘 (850px 이하에서 아이콘)
    } else {
      // 알람 페이지에서는 기존 3단계 유지
      if (windowWidth > 850) return 'name';      // 1단계: 클래스명
      if (windowWidth > 420) return 'icon';      // 2단계: 클래스 아이콘
      return 'none';                             // 3단계: 사라지기
    }
  };

  return (
    <HeaderContainer $isHome={currentPage === 'home'}>
      <LeftSection>
        <LogoIcon onClick={() => onNavigate('home')}>
          <img src="/LOGOeducodi_new.svg" alt="Logo" className="logo-full" />
          <img src="/LOGOeducodi2_new.svg" alt="Logo" className="logo-compact" />
        </LogoIcon>
        <ClassManageContainer onClick={handleClassManageClick}>
          {getDisplayStage() === 'name' && (
            <ClassNameDisplay>
              {currentClass ? currentClass.name : '클래스 없음'}
            </ClassNameDisplay>
          )}
          {getDisplayStage() === 'icon' && (
            <ClassIcon onClick={handleClassManageClick}>
              <svg 
                width="20" 
                height="20" 
                viewBox="0 0 24 24" 
                fill="none" 
                xmlns="http://www.w3.org/2000/svg"
              >
                <path 
                  d="M3 3h18v18H3V3zm2 2v14h14V5H5zm2 2h10v2H7V7zm0 4h10v2H7v-2zm0 4h6v2H7v-2z" 
                  fill="currentColor"
                />
              </svg>
            </ClassIcon>
          )}
        </ClassManageContainer>

        {showMemberGroupSelector && (
          <MemberGroupSelectWrapper
            onClick={(e) => {
              // 클래스 관리 모달 오픈과 충돌 방지
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            <MemberGroupSelect
              value={currentGroupValue}
              onChange={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleMemberGroupChange(e.target.value);
              }}
            >
              {normalizedMemberGroups.map((g, index) => (
                <option
                  key={g}
                  value={g}
                  disabled={!canSelectAllMemberGroups && index > 0}
                >
                  {getGroupDisplayName(g)}
                </option>
              ))}
            </MemberGroupSelect>
          </MemberGroupSelectWrapper>
        )}
      </LeftSection>
      
      {currentPage !== 'home' && (
        <CenterSection>
          <Title>
            {currentPage === 'assignments' ? '과제 관리' :
             currentPage === 'alarm' ? '활동 시작 알람' :
             currentPage === 'points-table' ? '포인트 코인 조회' :
             currentPage === 'chat' ? '클래스 챗' :
             currentPage === 'board' ? '보드' :
             currentPage === 'license' ? '이용권 구매 및 관리' :
             currentPage === 'terms-of-service' ? '서비스 이용약관' :
             currentPage === 'privacy-policy' ? '개인정보처리방침' :
             '포인트 조회 및 관리'}
          </Title>
        </CenterSection>
      )}
      
      <RightSection>
        <UserInfo>
          <UserName>{user.name} 님</UserName>
        </UserInfo>
        <HamburgerButton 
          className="hamburger-menu"
          onClick={handleHamburgerClick}
        >
          <svg 
            width="20" 
            height="20" 
            viewBox="0 0 24 24" 
            fill="none" 
            xmlns="http://www.w3.org/2000/svg"
          >
            <path 
              d="M3 12h18M3 6h18M3 18h18" 
              stroke="currentColor" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            />
          </svg>
        </HamburgerButton>
        
        {/* 햄버거 메뉴 드롭다운 */}
      {isMenuOpen && (
        <MenuDropdown className="menu-dropdown">
          <MenuIdentityHeader>
            <MenuIdentityTopRow>
              <MenuAvatarRing>
                <MenuAvatarImage
                  src={profileImageSrc}
                  alt=""
                  referrerPolicy="no-referrer"
                  onError={() => setAvatarBroken(true)}
                />
              </MenuAvatarRing>
              <MenuLogoutButton
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsMenuOpen(false);
                  onLogout();
                }}
              >
                로그아웃
              </MenuLogoutButton>
            </MenuIdentityTopRow>
            <MenuIdentityName>{user.name}</MenuIdentityName>
            {user.email ? <MenuIdentityEmail>{user.email}</MenuIdentityEmail> : null}
            {showStorageUsage ? (
              <MenuStorageBlock>
                <MenuStorageLabel>
                  {storageUsageLoading
                    ? '사용량 확인 중…'
                    : storageUsedBytes != null
                      ? `${userStorageQuotaService.formatBytes(storageUsedBytes)} / ${userStorageQuotaService.quotaLabel}`
                      : `— / ${userStorageQuotaService.quotaLabel}`}
                </MenuStorageLabel>
                <MenuStorageGauge aria-hidden>
                  <MenuStorageGaugeFill
                    $percent={
                      storageUsedBytes != null
                        ? Math.min(100, (storageUsedBytes / userStorageQuotaService.quotaBytes) * 100)
                        : 0
                    }
                    $warning={
                      storageUsedBytes != null &&
                      storageUsedBytes / userStorageQuotaService.quotaBytes >= 0.85
                    }
                  />
                </MenuStorageGauge>
              </MenuStorageBlock>
            ) : null}
          </MenuIdentityHeader>

          {/* 설정 (프로필 모달) */}
          <MenuItem
            onClick={(e) => {
              console.log('🔍 Header: 설정 클릭');
              e.preventDefault();
              e.stopPropagation();
              onProfileModalOpen();
              setIsMenuOpen(false);
            }}
            style={{ fontWeight: 'bold' }}
          >
            설정
          </MenuItem>
          
          {/* 이용권 구매 및 관리 */}
          <MenuItem
            onClick={(e) => {
              console.log('🔍 Header: 이용권 구매 및 관리 클릭');
              e.preventDefault();
              e.stopPropagation();
              onNavigate('license');
              setIsMenuOpen(false);
            }}
          >
            이용권 구매 및 관리
          </MenuItem>
          
          <MenuDivider />
          
          {/* 기본 환경설정 */}
          {onAssignmentSettingsClick && (
            <MenuItem
              onClick={(e) => {
                console.log('🔍 Header: 기본 환경설정 클릭');
                e.preventDefault();
                e.stopPropagation();
                console.log('🔍 Header: 클릭으로 핸들러 호출 시도');
                try {
                  onAssignmentSettingsClick();
                  console.log('🔍 Header: 클릭으로 기본 환경설정 핸들러 실행 완료');
                } catch (error) {
                  console.error('🔍 Header: 클릭 핸들러 실행 오류:', error);
                }
                setIsMenuOpen(false);
                console.log('🔍 Header: 클릭으로 드롭다운 닫힘');
              }}
              style={{ cursor: 'pointer', userSelect: 'none' }}
            >
              <MenuItemInline>
                <MenuGroupBadge>클래스</MenuGroupBadge>
                <span>기본 환경설정</span>
              </MenuItemInline>
            </MenuItem>
          )}

          {currentClass && (
            <MenuItem
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleMemberPhotoProfileSettingsOpen();
                setIsMenuOpen(false);
              }}
              style={{ cursor: 'pointer', userSelect: 'none' }}
            >
              <MenuItemInline>
                <MenuGroupBadge>그룹</MenuGroupBadge>
                <span>사진 프로필 설정</span>
              </MenuItemInline>
            </MenuItem>
          )}

          {/* 포인트/보드 페이지는 각 화면에 뽑기 진입이 있어 햄버거 메뉴 항목은 숨김 */}
          {onDiceModalOpen &&
            currentPage !== 'points-table' &&
            currentPage !== 'board' && (
            <MenuItem
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onDiceModalOpen();
                setIsMenuOpen(false);
              }}
              style={{ cursor: 'pointer', userSelect: 'none' }}
            >
              뽑기
            </MenuItem>
          )}
          
          {/* 자리 배치 설정 */}
          {onSeatAssignmentClick && (
            <MenuItem
              onClick={(e) => {
                console.log('🔍 Header: 자리 배치 설정 클릭');
                e.preventDefault();
                e.stopPropagation();
                console.log('🔍 Header: 클릭으로 핸들러 호출 시도');
                try {
                  onSeatAssignmentClick();
                  console.log('🔍 Header: 클릭으로 자리 배치 설정 핸들러 실행 완료');
                } catch (error) {
                  console.error('🔍 Header: 클릭 핸들러 실행 오류:', error);
                }
                setIsMenuOpen(false);
                console.log('🔍 Header: 클릭으로 드롭다운 닫힘');
              }}
              style={{ cursor: 'pointer', userSelect: 'none' }}
            >
              <MenuItemInline>
                <MenuGroupBadge>그룹</MenuGroupBadge>
                <span>자리 배치 설정</span>
              </MenuItemInline>
            </MenuItem>
          )}
          
          {/* 포인트 세트 설정 */}
          {onPointManagementClick && (
            <MenuItem
              onClick={(e) => {
                console.log('🔍 Header: 포인트 세트 설정 클릭');
                e.preventDefault();
                e.stopPropagation();
                console.log('🔍 Header: 클릭으로 핸들러 호출 시도');
                try {
                  onPointManagementClick();
                  console.log('🔍 Header: 클릭으로 포인트 세트 설정 핸들러 실행 완료');
                } catch (error) {
                  console.error('🔍 Header: 클릭 핸들러 실행 오류:', error);
                }
                setIsMenuOpen(false);
                console.log('🔍 Header: 클릭으로 드롭다운 닫힘');
              }}
              style={{ cursor: 'pointer', userSelect: 'none' }}
            >
              <MenuItemInline>
                <MenuGroupBadge>클래스</MenuGroupBadge>
                <span>포인트 세트 설정</span>
              </MenuItemInline>
            </MenuItem>
          )}
          
          {/* 아이템 카드 설정 */}
          {onItemCardSettingsClick && (
            <MenuItem
              onClick={(e) => {
                console.log('🔍 Header: 아이템 카드 설정 클릭');
                e.preventDefault();
                e.stopPropagation();
                console.log('🔍 Header: 클릭으로 핸들러 호출 시도');
                try {
                  onItemCardSettingsClick();
                  console.log('🔍 Header: 클릭으로 아이템 카드 설정 핸들러 실행 완료');
                } catch (error) {
                  console.error('🔍 Header: 클릭 핸들러 실행 오류:', error);
                }
                setIsMenuOpen(false);
                console.log('🔍 Header: 클릭으로 드롭다운 닫힘');
              }}
              style={{ cursor: 'pointer', userSelect: 'none' }}
            >
              <MenuItemInline>
                <MenuGroupBadge>클래스</MenuGroupBadge>
                <span>아이템 카드 설정</span>
              </MenuItemInline>
            </MenuItem>
          )}
          
          {/* 레벨 시스템 설정 */}
          {onLevelSettingsClick && (
            <MenuItem
              onClick={(e) => {
                console.log('🔍 Header: 레벨 시스템 설정 클릭');
                e.preventDefault();
                e.stopPropagation();
                console.log('🔍 Header: 클릭으로 핸들러 호출 시도');
                try {
                  onLevelSettingsClick();
                  console.log('🔍 Header: 클릭으로 레벨 시스템 설정 핸들러 실행 완료');
                } catch (error) {
                  console.error('🔍 Header: 클릭 핸들러 실행 오류:', error);
                }
                setIsMenuOpen(false);
                console.log('🔍 Header: 클릭으로 드롭다운 닫힘');
              }}
              style={{ cursor: 'pointer', userSelect: 'none' }}
            >
              <MenuItemInline>
                <MenuGroupBadge>클래스</MenuGroupBadge>
                <span>레벨 시스템 설정</span>
              </MenuItemInline>
            </MenuItem>
          )}
          <MenuDivider />
          <MenuFooter>
            <NavThemeToggleButton
              $isDark={themeMode === 'dark'}
              onClick={(e) => e.stopPropagation()}
              aria-label="테마 전환"
            >
              <NavThemeToggleOption
                $isActive={themeMode === 'light'}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (themeMode === 'dark') onToggleTheme();
                }}
              >
                일반
              </NavThemeToggleOption>
              <NavThemeToggleOption
                $isActive={themeMode === 'dark'}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (themeMode === 'light') onToggleTheme();
                }}
              >
                다크
              </NavThemeToggleOption>
            </NavThemeToggleButton>
            <PageZoomRow onClick={(e) => e.stopPropagation()}>
              <PageZoomButton
                type="button"
                onClick={handlePageZoomOut}
                disabled={pageZoom <= PAGE_ZOOM_MIN}
                aria-label="페이지 축소"
              >
                −
              </PageZoomButton>
              <PageZoomPercent aria-live="polite">{formatPageZoomPercent(pageZoom)}</PageZoomPercent>
              <PageZoomButton
                type="button"
                onClick={handlePageZoomIn}
                disabled={pageZoom >= PAGE_ZOOM_MAX}
                aria-label="페이지 확대"
              >
                +
              </PageZoomButton>
            </PageZoomRow>
          </MenuFooter>
        </MenuDropdown>
      )}
      </RightSection>

    </HeaderContainer>
  );
};

const HeaderContainer = styled.header<{ $isHome: boolean }>`
  background: ${props => props.$isHome ? 'transparent' : 'var(--color-accent-strong)'};
  color: ${props => props.$isHome ? 'white' : 'white'};
  padding: 4px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: ${props => props.$isHome ? 'none' : '0 2px 4px rgba(0, 0, 0, 0.1)'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 9000;
  height: 36px;
  box-sizing: border-box;
  
  /* CSS 변수로 헤더 높이를 전역적으로 관리 */
  --header-height: 36px;
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ClassManageContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  transition: background-color 0.2s ease;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }
  
  @media (max-width: 850px) {
    padding: 0;
    background: none;
    
    &:hover {
      background: none;
    }
  }
`;

const ClassNameDisplay = styled.span`
  color: white;
  font-size: 1.1rem;
  font-weight: 500;
`;

const ClassIcon = styled.div`
  width: 32px;
  height: 32px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    border-color: rgba(255, 255, 255, 0.3);
  }
`;

const MemberGroupSelectWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const MemberGroupSelect = styled.select`
  height: 24px;
  padding: 0 24px 0 8px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.25);
  background: rgba(255, 255, 255, 0.12);
  color: #ffffff;
  font-size: 0.8rem;
  font-weight: 700;
  cursor: pointer;
  outline: none;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  background-color: rgba(255, 255, 255, 0.12);
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 6px center;
  background-size: 12px;

  &:hover {
    background-color: rgba(255, 255, 255, 0.18);
    border-color: var(--color-block-border);
  }

  &:focus-visible {
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.25);
  }

  option {
    color: var(--color-text-primary);
    background: var(--color-surface);
  }
`;

const CenterSection = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  justify-content: center;
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
`;

const Title = styled.h1`
  font-size: 1.0rem;
  font-weight: bold;
  margin: 0;
  text-align: center;
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  position: relative;
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  border-radius: 4px;
  
  @media (max-width: 850px) {
    display: none;
  }
`;

const UserName = styled.span`
  font-size: 1.0rem;
  font-weight: 500;
  
  @media (max-width: 850px) {
    display: none;
  }
`;

const LogoIcon = styled.div`
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    opacity: 0.8;
  }
  
  .logo-full {
    width: 60px;
    height: 14px;
    object-fit: contain;
    display: block;
  }

  .logo-compact {
    width: 24px;
    height: 24px;
    object-fit: contain;
    display: none;
  }

  @media (max-width: 850px) {
    .logo-full {
      display: none;
    }

    .logo-compact {
      display: block;
      width: 24px;
      height: 24px;
    }
  }
`;

const HamburgerButton = styled.button`
  width: 28px;
  height: 28px;
  border: none;
  background: transparent;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.12);
  }

  :root[data-theme='light'] & {
    &:hover {
      background: rgba(0, 0, 0, 0.12);
    }
  }
  
  &:focus {
    outline: none;
  }
  
  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.3);
  }
`;

const MenuDropdown = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 9999;
  width: 220px;
  overflow: hidden;
  margin-top: 4px;
  transform: translateX(0);
`;

const MenuIdentityHeader = styled.div`
  padding: 14px 16px 13px;
  text-align: left;
  border-bottom: 1px solid var(--color-border);
  /* 일반: #E8E8E8 → 흰색 / 다크: #1A1A1A → #232323 */
  background: #232323;

  :root[data-theme='light'] & {
    background: #ffffff;
  }
`;

const MenuIdentityTopRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
`;

const MenuAvatarRing = styled.div`
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  overflow: hidden;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  box-sizing: border-box;
`;

const MenuAvatarImage = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const MenuLogoutButton = styled.button`
  margin-left: auto;
  flex-shrink: 0;
  padding: 6px 10px;
  border-radius: 6px;
  border: 1px solid var(--color-border);
  background: var(--color-surface);
  color: var(--color-text-primary);
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;

  &:hover {
    background: var(--color-panel);
    border-color: var(--color-border-strong);
  }

  &:focus-visible {
    outline: 2px solid var(--color-accent-strong);
    outline-offset: 2px;
  }
`;

const MenuIdentityName = styled.div`
  font-size: 1rem;
  font-weight: 700;
  color: var(--color-text-primary);
  line-height: 1.3;
  overflow-wrap: anywhere;
`;

const MenuIdentityEmail = styled.div`
  margin-top: 5px;
  font-size: 0.75rem;
  font-weight: 400;
  color: var(--color-text-muted);
  line-height: 1.35;
  overflow-wrap: anywhere;
`;

const MenuStorageBlock = styled.div`
  margin-top: 12px;
  width: 100%;
`;

const MenuStorageLabel = styled.div`
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--color-text-secondary);
  line-height: 1.35;
  margin-bottom: 6px;
`;

const MenuStorageGauge = styled.div`
  width: 100%;
  height: 6px;
  border-radius: 999px;
  background: var(--color-border);
  overflow: hidden;
`;

const MenuStorageGaugeFill = styled.div<{ $percent: number; $warning: boolean }>`
  width: ${(p) => p.$percent}%;
  height: 100%;
  border-radius: 999px;
  background: ${(p) => (p.$warning ? '#e67e22' : 'var(--color-accent-strong)')};
  transition: width 0.25s ease;
`;

const MenuItem = styled.button`
  width: 100%;
  padding: 12px 16px;
  border: none;
  background: var(--color-surface);
  color: var(--color-text-primary);
  font-size: 14px;
  text-align: left;
  /* 버튼 내부 플렉 자식 정렬: Safari 등에서 배지+텍스트 행이 깨지지 않도록 */
  display: flex;
  align-items: center;
  justify-content: flex-start;
  cursor: pointer;
  transition: background-color 0.2s ease;
  position: relative;
  z-index: 10000;
  pointer-events: auto;
  min-height: 44px; /* 모바일 터치 영역을 위한 최소 높이 */

  &:hover {
    background: var(--color-panel);
  }

  :root[data-theme='light'] & {
    &:hover {
      background: var(--color-surface-highlight);
    }
  }

  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: none;
    box-shadow: inset 0 0 0 2px rgba(var(--color-accent-rgb), 0.3);
  }

  &:active {
    background: var(--color-border);
  }
`;

const MenuDivider = styled.div`
  width: 100%;
  height: 0;
  border-top: 1px solid var(--color-border);
  flex-shrink: 0;
`;

const MenuItemInline = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
  width: 100%;

  /* 배지 다음 텍스트만 줄어들 수 있게 (좁은 메뉴 폭에서 배지는 유지) */
  > span:last-of-type {
    min-width: 0;
    flex: 1;
    text-align: left;
  }
`;

const MenuGroupBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  flex-shrink: 0;
  min-width: 48px;
  height: 20px;
  padding: 0 7px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.1px;
  line-height: 1;
  white-space: nowrap;
  /* 다크 모드: 배지가 잘 보이도록 밝은 회색 톤 */
  color: #d8d8d8;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.42);

  :root[data-theme='light'] & {
    color: var(--color-accent-strong);
    background: rgba(var(--color-accent-rgb), 0.16);
    border: 1px solid rgba(var(--color-accent-rgb), 0.34);
  }
`;

const MenuFooter = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: stretch;
  padding: 12px 16px;
  background: var(--color-panel);
`;

const PageZoomRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  min-height: 26px;
`;

const PageZoomButton = styled.button`
  width: 32px;
  height: 26px;
  min-width: 32px;
  min-height: 26px;
  flex-shrink: 0;
  border: 1px solid var(--color-border);
  border-radius: 8px;
  background: var(--color-surface);
  color: var(--color-text-primary);
  font-size: 16px;
  font-weight: 700;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  transition: background 0.2s ease, border-color 0.2s ease, opacity 0.2s ease;
  -webkit-appearance: none;
  appearance: none;

  &:hover:not(:disabled) {
    background: var(--color-panel);
    border-color: var(--color-border-strong);
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const PageZoomPercent = styled.div`
  flex: 1;
  text-align: center;
  font-size: 12px;
  font-weight: 700;
  color: var(--color-text-secondary);
  font-variant-numeric: tabular-nums;
  user-select: none;
`;

const NavThemeToggleButton = styled.button<{ $isDark: boolean }>`
  display: flex;
  flex: 1;
  width: 100%;
  background: var(--color-accent-strong);
  border-radius: 13px;
  padding: 2px;
  position: relative;
  cursor: pointer;
  transition: all 0.3s ease;
  height: 26px;
  min-width: 80px;
  min-height: 26px;
  box-sizing: border-box;
  border: none;
  appearance: none;
  -webkit-appearance: none;

  /* 라이트 모드: 배경을 흰색으로 */
  :root[data-theme='light'] & {
    background: #ffffff;
    border: 1px solid var(--color-border);
  }

  &::before {
    content: '';
    position: absolute;
    top: 2px;
    left: ${props => props.$isDark ? '50%' : '2px'};
    width: calc(50% - 2px);
    height: calc(100% - 4px);
    background: var(--color-surface);
    border-radius: 11px;
    transition: left 0.3s ease;
    z-index: 1;
  }

  /* 라이트 모드: 슬라이더 도형을 메인테마 초록색으로 */
  :root[data-theme='light'] &::before {
    background: var(--color-accent-strong);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const NavThemeToggleOption = styled.div<{ $isActive: boolean }>`
  flex: 1;
  padding: 2px 10px;
  font-size: 11px !important;
  font-weight: 600;
  color: ${props => props.$isActive ? 'var(--color-toggle-active)' : 'var(--color-toggle-inactive)'};
  text-align: center;
  position: relative;
  z-index: 2;
  transition: color 0.3s ease;
  user-select: none;
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  cursor: pointer;

  /* 라이트 모드: 활성 옵션은 흰색, 비활성은 반투명 초록색 */
  :root[data-theme='light'] & {
    color: ${props => props.$isActive ? '#ffffff' : 'rgba(var(--color-accent-rgb), 0.6)'};
  }
`;

export default Header; 